/**
 * Receives first-party analytics beacons. Stores no IP, no personal data.
 * Inserts go through the server (service role) so the table needs no public INSERT policy.
 */
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { isAllowedOrigin } from "@/lib/contact-abuse.server";
import { ANALYTICS_EVENTS, BOT_LABELS, normalizePage } from "@/lib/analytics-shared";

const primitive = z.union([z.string().max(80), z.number().finite(), z.boolean()]);
const beaconSchema = z.object({
  sessionId: z.string().min(8).max(64),
  visitorId: z.string().min(8).max(64).nullable(),
  source: z.string().max(20),
  device: z.enum(["mobile", "tablet", "desktop"]),
  bot: z.enum(BOT_LABELS),
  interacted: z.boolean(),
  events: z
    .array(
      z.object({
        event: z.enum(ANALYTICS_EVENTS),
        page: z.string().min(1).max(200),
        lang: z.enum(["nl", "en", "fr"]).nullable(),
        label: z.string().max(80).nullable().optional(),
        itemId: z.string().max(80).nullable().optional(),
        props: z.record(z.string().max(40), primitive).optional(),
        t: z.string().datetime(),
      }),
    )
    .min(1)
    .max(40),
});

export const Route = createFileRoute("/api/public/analytics")({
  staticData: { sitemap: false },
  server: {
    handlers: {
      POST: async ({ request }) => {
        const ok = new Response(null, { status: 204 });
        if (!isAllowedOrigin(request.headers.get("origin"), request.url)) return ok;
        let parsed;
        try {
          parsed = beaconSchema.parse(JSON.parse(await request.text()));
        } catch {
          return ok;
        }
        const now = Date.now();
        const rows = parsed.events.map((e) => {
          const at = Date.parse(e.t);
          return {
            created_at: Math.abs(now - at) < 10 * 60_000 ? e.t : new Date(now).toISOString(),
            event: e.event,
            page: normalizePage(e.page),
            lang: e.lang,
            label: e.label ?? null,
            item_id: e.itemId ?? null,
            session_id: parsed.sessionId,
            visitor_id: parsed.visitorId,
            source: parsed.source,
            device: parsed.device,
            is_bot: parsed.bot,
            interacted: parsed.interacted,
            props: e.props ?? {},
          };
        });
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { error } = await supabaseAdmin.from("analytics_events").insert(rows);
        if (error) console.error("[analytics] insert failed:", error.message);
        return ok;
      },
    },
  },
});
